import { QueryClient, } from '@tanstack/react-query'
import { isProd, createReactQuerySpanEvent, hashQueryKeyWithoutTel } from '@w/fe-utils'
import { isAuthConnectionProblem } from './fe-inv-domain-errors'

/**
 * Not on a connection problem: a retry could only fail the same way until authSession's recovery
 * loop gets a token; the failed queries refetch once it does
 */
export const defaultQueryRetry = (failureCount: number, error: unknown): boolean =>
  isProd && failureCount < 3 && !isAuthConnectionProblem(error)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: defaultQueryRetry,
      queryKeyHashFn: hashQueryKeyWithoutTel,
    },
  },
})

export const getQueryClient = (opts?: { queryClient?: QueryClient }) =>
  opts?.queryClient ?? queryClient

/**
 * Refetches every query that failed, the inactive ones too: a component that suspended on a query
 * and failed never mounted, so its query has no observer until its error boundary resets.
 */
export const refetchFailedQueries = (queryClient: QueryClient) =>
  queryClient.refetchQueries(
    { type: 'all', predicate: (query) => query.state.status === 'error' },
    { cancelRefetch: false },
  )

/**
 * For a token of another user than the last one (embedded-session-source.ts): nothing fetched for
 * the previous user may reach the next. Resetting cancels each query's fetch in flight
 * silently, so its answer never lands, and refetches the active queries, with the new user's token.
 */
export const resetForNewUser = (queryClient: QueryClient) => queryClient.resetQueries()

queryClient.getQueryCache().subscribe((event) => createReactQuerySpanEvent(event))
